/**
 * Scrolled-nav pill sizing.
 *
 * The scrolled nav collapses from a full-width bar into a centred glass
 * island. That island's width used to be a hard-coded 730px
 * (`calc(50% - 365px)` on both insets), measured by hand against the nav
 * content of the day. Adding one nav item pushed the content past 730px
 * and the "Book a call" CTA hung outside the plate.
 *
 * This measures the real content once (and again on resize / after the
 * webfont lands) and writes the half-width into `--nav-pill-half`, which
 * the CSS reads inside the SAME max()/calc() shape it already used — so
 * the collapse still tweens instead of snapping.
 *
 * The insets stay written as max(20px, …), so the pill can never grow
 * past the viewport: it just stops hugging and takes the 20px gutters.
 */
export function initNavPill (nav) {
  if (!nav) return

  // Below 768px the pill rule doesn't apply at all (phone keeps the
  // compact full-width bar), so leave the variable unset there.
  const MIN_WIDTH = 768

  /**
   * Padding / gap / border differ between the two states, and the pill
   * is sized for the SCROLLED one. Read them off the real class rather
   * than duplicating the numbers here — a CSS tweak then can't drift
   * away from the measurement. Transitions are suppressed across the
   * probe so flipping the class can't fire the collapse animation.
   */
  function measure () {
    const hadClass = nav.classList.contains('is-scrolled')
    const prevTransition = nav.style.transition
    nav.style.transition = 'none'
    if (!hadClass) nav.classList.add('is-scrolled')

    const cs = getComputedStyle(nav)
    const gap = parseFloat(cs.columnGap) || 0
    const chrome =
      parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) +
      parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth)

    // Only the children that actually occupy the row — the burger is
    // display:none on desktop and must not earn a gap.
    const kids = Array.from(nav.children).filter((el) => el.offsetWidth > 0)
    const content = kids.reduce((sum, el) => sum + el.getBoundingClientRect().width, 0)

    if (!hadClass) nav.classList.remove('is-scrolled')
    void nav.offsetWidth // flush the restore before transitions come back
    nav.style.transition = prevTransition

    return content + gap * Math.max(0, kids.length - 1) + chrome
  }

  function apply () {
    if (window.innerWidth < MIN_WIDTH) {
      nav.style.removeProperty('--nav-pill-half')
      return
    }
    // +2px so sub-pixel text widths can't round the plate a hair narrow
    // than the content it's meant to hug.
    nav.style.setProperty('--nav-pill-half', Math.ceil((measure() + 2) / 2) + 'px')
  }

  apply()

  // Satoshi loads after first paint; the measurement taken in the
  // fallback face is a few px off, so redo it once the real one lands.
  document.fonts?.ready.then(apply).catch(() => {})

  let frame = 0
  window.addEventListener('resize', () => {
    if (frame) return
    frame = requestAnimationFrame(() => { frame = 0; apply() })
  }, { passive: true })
}
